import { useEffect, useState, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet";

import { request } from "@/Api";
import { PERMISSION_VIEW_CONFIDENTIAL_DATA } from "@/auth/constants";
import useEntitlements from "@/auth/useEntitlements";
import { PageTitle } from "@/common/CommonComponents";
import {
    FEATURE_ACCOUNTING_STRATEGY,
    FEATURE_RESOURCING_STRATEGY,
} from "@/constants";
import ProjectFilters from "@/projects/ProjectFilters";
import ProjectRow from "@/projects/ProjectRow";
import CustomTableContainer from "@/table//CustomTableContainer";
import { headCells } from "./headCells";

export default function Projects({
    prefixedFilters,
    prefixedTableViewFields,
    root,
}) {
    const { hasFeature, hasPermission } = useEntitlements();
    const hasResourcingStrategy = hasFeature(FEATURE_RESOURCING_STRATEGY);
    const hasAccountingStrategy = hasFeature(FEATURE_ACCOUNTING_STRATEGY);
    const confidentialDataPermission = hasPermission(
        PERMISSION_VIEW_CONFIDENTIAL_DATA,
    );

    const [serverTableFields, setServerTableFields] = useState([]);

    const getTableView = useCallback(() => {
        return request.get("/table_view/project").then((response) => {
            let fields = response.data.fields.split(",");

            if (!confidentialDataPermission) {
                fields = fields.map((field) =>
                    field === "docket" ? "docket_restricted" : field,
                );
            }

            // TODO: consider less hardcoded ways:
            // create "table_field" db table, and have strategy_id per field
            // create backend service for filtering by strategy
            // create table_view entry by strategies, and maintain the table_view by switch in strategy
            fields = fields.filter((field) => {
                if (field === "accounting_rev" && !hasAccountingStrategy)
                    return false;
                if (field === "accounting_cost" && !hasAccountingStrategy)
                    return false;
                if (field === "float_internal_actual" && !hasResourcingStrategy)
                    return false;
                if (
                    field === "float_internal_scheduled" &&
                    !hasResourcingStrategy
                )
                    return false;
                return true;
            });

            setServerTableFields(fields);
        });
    }, [
        confidentialDataPermission,
        hasAccountingStrategy,
        hasResourcingStrategy,
    ]);

    const tableFields = useMemo(
        () => prefixedTableViewFields ?? serverTableFields,
        [prefixedTableViewFields, serverTableFields],
    );

    const tableHeadCells = useMemo(
        () => tableFields.map((field) => headCells[field]).filter(Boolean),
        [tableFields],
    );

    useEffect(() => {
        if (!prefixedTableViewFields) {
            getTableView();
        }
    }, [prefixedTableViewFields, getTableView]);

    return (
        <CustomTableContainer
            prefixedFilters={prefixedFilters}
            modelType="project"
            defaultOrder="-docket"
            title="Projects"
            headCells={tableHeadCells}
            itemRowComponent={ProjectRow}
            itemRowProps={{
                tableFields,
            }}
            filterComponents={ProjectFilters}
        >
            {root && (
                <Helmet>
                    <PageTitle title="Projects" />
                </Helmet>
            )}
        </CustomTableContainer>
    );
}
